import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  AdminPage,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useModule } from '@/access/useModule';
import { errorMessage } from '@/lib/errors';
import { findList, type Field } from './registry';
import { pkOf, useListMutations, useListRows, type Row } from './api';
import { RowForm } from './RowForm';

/**
 * מסך ניהול גנרי לכל טבלת רשימה (PRD 5.13).
 * אותו מסך משרת את כל הרשימות, לפי התיאור ב registry.
 */
/**
 * `listKey` למי שמגיע מנתיב קבוע ולא מ /admin/settings/:list.
 * רשימה יכולה לשבת גם בטאב שלה, שם מחפשים אותה.
 *
 * `backTo` הולך יחד איתו: רשימה שנפתחת מתוך טאב צריכה להחזיר
 * לאותו טאב. בלעדיו "חזרה" הוביל להגדרות, כלומר הוציא את מי
 * שבכלל לא היה שם.
 */
export function ListPage({
  listKey,
  backTo = '/admin/settings',
}: { listKey?: string; backTo?: string } = {}) {
  const { list } = useParams();
  const def = findList(listKey ?? list);

  if (!def) return <Navigate to="/admin/settings" replace />;
  return <ListScreen defKey={def.key} backTo={backTo} />;
}

function ListScreen({ defKey, backTo }: { defKey: string; backTo: string }) {
  const def = findList(defKey)!;
  const canEdit = usePerm(def.perm);
  const moduleOn = useModule(def.module ?? 'messages');
  const isVisible = def.module ? moduleOn : true;

  const pk = pkOf(def);
  const { data: rows, isPending, isError, refetch } = useListRows(def);
  const { create, update, remove, restore, swapOrder } = useListMutations(def);

  const [editing, setEditing] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (!isVisible) return <Navigate to="/admin/settings" replace />;

  async function runAction(action: () => Promise<unknown>) {
    setActionError(null);
    try {
      await action();
    } catch (caught) {
      setActionError(errorMessage(caught));
    }
  }

  return (
    <AdminPage backTo={backTo} title={def.label} subtitle={def.description}>
      {canEdit && !isAdding && (
        <div>
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            הוספה
          </Button>
        </div>
      )}

      {isAdding && (
        <RowForm
          def={def}
          onCancel={() => setIsAdding(false)}
          onSubmit={async (values) => {
            await create.mutateAsync(values);
            setIsAdding(false);
          }}
        />
      )}

      {actionError && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {actionError}
        </p>
      )}

      {isPending && (
        <div className="grid gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {isError && <ErrorState onRetry={() => void refetch()} />}

      {rows && rows.length === 0 && (
        <EmptyState
          title="הרשימה ריקה"
          description={canEdit ? 'אפשר להוסיף פריט ראשון.' : 'אין כאן פריטים כרגע.'}
        />
      )}

      {rows && rows.length > 0 && (
        <ul className="grid list-none gap-2 p-0">
          {rows.map((row, index) => (
            <li key={String(row[pk])} className="rounded-md border border-line bg-raised p-3">
              {editing === String(row[pk]) ? (
                <RowForm
                  def={def}
                  row={row}
                  onCancel={() => setEditing(null)}
                  onSubmit={async (values) => {
                    await update.mutateAsync({ id: String(row[pk]), values });
                    setEditing(null);
                  }}
                />
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <RowSummary def={def} row={row} />
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-1">
                      {def.sortable && (
                        <>
                          <IconButton
                            label="הזזה למעלה"
                            disabled={index === 0}
                            onClick={() =>
                              runAction(() =>
                                swapOrder.mutateAsync({
                                  a: row,
                                  b: rows[index - 1],
                                }),
                              )
                            }
                          >
                            <ChevronUp size={20} strokeWidth={1.75} aria-hidden="true" />
                          </IconButton>
                          <IconButton
                            label="הזזה למטה"
                            disabled={index === rows.length - 1}
                            onClick={() =>
                              runAction(() =>
                                swapOrder.mutateAsync({
                                  a: row,
                                  b: rows[index + 1],
                                }),
                              )
                            }
                          >
                            <ChevronDown size={20} strokeWidth={1.75} aria-hidden="true" />
                          </IconButton>
                        </>
                      )}

                      <IconButton label="עריכה" onClick={() => setEditing(String(row[pk]))}>
                        <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                      </IconButton>

                      {def.deactivatable && row.is_active === false ? (
                        <IconButton
                          label="החזרה לפעילות"
                          onClick={() => runAction(() => restore.mutateAsync(String(row[pk])))}
                        >
                          <RotateCcw size={20} strokeWidth={1.75} aria-hidden="true" />
                        </IconButton>
                      ) : (
                        <IconButton
                          label={def.deactivatable ? 'השבתה' : 'מחיקה'}
                          onClick={() => runAction(() => remove.mutateAsync(String(row[pk])))}
                        >
                          <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </IconButton>
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!canEdit && (
        <p className="m-0 text-body-sm text-muted">אפשר לצפות ברשימה. לעריכה צריך הרשאה מתאימה.</p>
      )}
    </AdminPage>
  );
}

function RowSummary({ def, row }: { def: { fields: Field[] }; row: Row }) {
  const [first, ...rest] = def.fields;
  const details = rest
    .map((field) => formatValue(field, row[field.name]))
    .filter((text): text is string => text !== null);

  return (
    <div className="grid gap-1">
      <span className="flex items-center gap-2 text-title font-medium">
        {String(row[first.name] ?? '')}
        {row.is_active === false && <StatusBadge tone="warn">מושבת</StatusBadge>}
      </span>
      {details.length > 0 && <span className="text-body-sm text-muted">{details.join(' · ')}</span>}
    </div>
  );
}

function formatValue(field: Field, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'boolean') return value === true ? field.label : null;
  if (field.type === 'color') return null;
  if (field.type === 'time') return `${field.label} ${String(value).slice(0, 5)}`;
  if (field.type === 'select') {
    const option = (field.options ?? []).find((item) => item.value === String(value));
    return `${field.label}: ${option?.label ?? String(value)}`;
  }
  return `${field.label}: ${String(value)}`;
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
